import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CountUp from "react-countup";
import axios from "axios";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import JornadasMap from "./JornadasMap";

const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2.5),
  },
  borderRadius: "20px",
  backgroundColor: theme.palette.background.alt,
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
  height: "100%",
}));

const FilterCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[600] : "#f8fbfb",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  borderRadius: "16px",
  padding: theme.spacing(1.5),
  height: "100%",
}));

const getMockDeploymentName = (number) => `${MEDICAL_DEPLOYMENT_LABELS.singularShort} ${number}`;

const mockData = [
  {
    fecha: "2026-06-20",
    codigo: "LG-000241",
    beneficiario: "María González",
    edad: 34,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Catia La Mar",
    comunidad: "Catia La Mar",
    centro: "Casa Comunitaria",
    jornada: getMockDeploymentName(1),
    motivoConsulta: "Cefalea y malestar",
    diagnostico: "Hipertensión",
    tipoEnfermedad: "Crónica",
    medico: "Dra. Pérez",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: true,
  },
  {
    fecha: "2026-06-21",
    codigo: "LG-000242",
    beneficiario: "José Martínez",
    edad: 62,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Maiquetía",
    comunidad: "Maiquetía",
    centro: "Módulo Central",
    jornada: getMockDeploymentName(1),
    motivoConsulta: "Dolor torácico",
    diagnostico: "Evaluación cardiovascular",
    tipoEnfermedad: "Cardiovascular",
    medico: "Dr. Rojas",
    estadoAtencion: "Referido",
    discapacidad: false,
    riesgo: true,
  },
  {
    fecha: "2026-06-22",
    codigo: "LG-000243",
    beneficiario: "Ana Rodríguez",
    edad: 11,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Macuto",
    comunidad: "Macuto",
    centro: "Ambulatorio Macuto",
    jornada: getMockDeploymentName(2),
    motivoConsulta: "Tos y fiebre",
    diagnostico: "Infección respiratoria",
    tipoEnfermedad: "Respiratoria",
    medico: "Dra. Núñez",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: false,
  },
  {
    fecha: "2026-06-23",
    codigo: "LG-000244",
    beneficiario: "Carlos Díaz",
    edad: 47,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Caraballeda",
    comunidad: "Caraballeda",
    centro: "Punto de Salud Caribe",
    jornada: getMockDeploymentName(2),
    motivoConsulta: "Dolor abdominal",
    diagnostico: "Síndrome gastrointestinal",
    tipoEnfermedad: "Digestiva",
    medico: "Dr. Salazar",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: false,
  },
  {
    fecha: "2026-06-24",
    codigo: "LG-000245",
    beneficiario: "Elena Suárez",
    edad: 71,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Catia La Mar",
    comunidad: "La Soublette",
    centro: "Punto Móvil",
    jornada: getMockDeploymentName(3),
    motivoConsulta: "Mareo y debilidad",
    diagnostico: "Descompensación",
    tipoEnfermedad: "Crónica",
    medico: "Dra. Pérez",
    estadoAtencion: "Observación",
    discapacidad: true,
    riesgo: true,
  },
  {
    fecha: "2026-06-24",
    codigo: "LG-000246",
    beneficiario: "Luis Alberto Torres",
    edad: 29,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Urimare",
    comunidad: "Urimare",
    centro: "Cancha Comunitaria",
    jornada: getMockDeploymentName(3),
    motivoConsulta: "Lesión en pierna",
    diagnostico: "Herida superficial",
    tipoEnfermedad: "Trauma",
    medico: "Dr. Moreno",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: false,
  },
  {
    fecha: "2026-06-25",
    codigo: "LG-000247",
    beneficiario: "Valeria Gómez",
    edad: 16,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Carayaca",
    comunidad: "Carayaca",
    centro: "Ambulatorio Rural",
    jornada: getMockDeploymentName(4),
    motivoConsulta: "Cefalea intensa",
    diagnostico: "Migraña",
    tipoEnfermedad: "Neurológica",
    medico: "Dra. Rivas",
    estadoAtencion: "Seguimiento",
    discapacidad: false,
    riesgo: false,
  },
  {
    fecha: "2026-06-25",
    codigo: "LG-000248",
    beneficiario: "José Rafael Blanco",
    edad: 63,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Naiguatá",
    comunidad: "Naiguatá",
    centro: "Escuela Naiguatá",
    jornada: getMockDeploymentName(4),
    motivoConsulta: "Glicemia elevada",
    diagnostico: "Diabetes descompensada",
    tipoEnfermedad: "Crónica",
    medico: "Dra. Salas",
    estadoAtencion: "Referido",
    discapacidad: true,
    riesgo: true,
  },
  {
    fecha: "2026-06-26",
    codigo: "LG-000249",
    beneficiario: "Sofía Peña",
    edad: 5,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Macuto",
    comunidad: "Macuto",
    centro: "Ambulatorio Macuto",
    jornada: getMockDeploymentName(5),
    motivoConsulta: "Fiebre",
    diagnostico: "Síndrome febril",
    tipoEnfermedad: "Infecciosa",
    medico: "Dra. Núñez",
    estadoAtencion: "Seguimiento",
    discapacidad: false,
    riesgo: true,
  },
  {
    fecha: "2026-06-26",
    codigo: "LG-000250",
    beneficiario: "Pedro Navarro",
    edad: 52,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Maiquetía",
    comunidad: "Maiquetía",
    centro: "Módulo Central",
    jornada: getMockDeploymentName(5),
    motivoConsulta: "Erupción cutánea",
    diagnostico: "Dermatitis",
    tipoEnfermedad: "Dermatológica",
    medico: "Dr. Salazar",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: false,
  },
  {
    fecha: "2026-06-27",
    codigo: "LG-000251",
    beneficiario: "Rosa Castillo",
    edad: 80,
    sexo: "F",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Catia La Mar",
    comunidad: "Catia La Mar",
    centro: "Casa Comunitaria",
    jornada: getMockDeploymentName(6),
    motivoConsulta: "Dificultad respiratoria",
    diagnostico: "Crisis asmática",
    tipoEnfermedad: "Respiratoria",
    medico: "Dra. Rivas",
    estadoAtencion: "Referido",
    discapacidad: false,
    riesgo: true,
  },
  {
    fecha: "2026-06-27",
    codigo: "LG-000252",
    beneficiario: "Miguel Hernández",
    edad: 38,
    sexo: "M",
    estado: "La Guaira",
    municipio: "Vargas",
    parroquia: "Caraballeda",
    comunidad: "Caraballeda",
    centro: "Punto de Salud Caribe",
    jornada: getMockDeploymentName(6),
    motivoConsulta: "Dolor lumbar",
    diagnostico: "Lumbalgia",
    tipoEnfermedad: "Trauma",
    medico: "Dr. Moreno",
    estadoAtencion: "Atendido",
    discapacidad: false,
    riesgo: false,
  },
];

const QA_DEFAULTS_ENABLED = false;

const QA_DEFAULT_FILTERS = {
  fechaDesde: "2026-07-01",
  fechaHasta: "2026-07-02",
  estado: "Zulia",
  codigoProyecto: "42",
  idConvocatoria: "917",
};

const initialFilters = {
  fechaDesde: QA_DEFAULTS_ENABLED ? QA_DEFAULT_FILTERS.fechaDesde : "",
  fechaHasta: QA_DEFAULTS_ENABLED ? QA_DEFAULT_FILTERS.fechaHasta : "",
  estadoId: "",
  estado: QA_DEFAULTS_ENABLED ? QA_DEFAULT_FILTERS.estado : "",
  municipioId: "",
  municipio: "",
  parroquiaId: "",
  parroquia: "",
  comunidad: "",
  centro: "",
  jornada: "",
  idConvocatoria: QA_DEFAULTS_ENABLED ? QA_DEFAULT_FILTERS.idConvocatoria : "",
  sexo: "",
  rangoEdad: "",
  medico: "",
  medicoId: "",
  estadoAtencion: "",
  codigoProyecto: QA_DEFAULTS_ENABLED ? QA_DEFAULT_FILTERS.codigoProyecto : "",
  busqueda: "",
  quickFilter: "",
};

const statusColor = {
  Atendido: "success",
  Seguimiento: "warning",
  Referido: "error",
  Observación: "info",
};

const chartColors = ["#009fe3", "#008080", "#f39313", "#22a06b", "#d94841", "#004f7c"];

const quickFilters = [
  { key: "today", label: "Hoy" },
  { key: "7days", label: "Últimos 7 días" },
  { key: "30days", label: "Últimos 30 días" },
  { key: "referidos", label: "Solo referidos" },
  { key: "reconsultas", label: "Solo reconsultas" },
  { key: "discapacidad", label: "Con discapacidad" },
  { key: "riesgo", label: "Factores de riesgo" },
  { key: "adultos", label: "Adultos mayores" },
  { key: "buscar", label: "Buscar beneficiario" },
];

const normalizeBackendRow = (row) => ({
  fecha: row.fecha_evaluacion,
  codigo: row.codigo,
  beneficiario: row.beneficiario || `${row.nombre || ""} ${row.apellido || ""}`.trim(),
  edad: row.edad,
  sexo: row.sexo,
  sexoOriginal: row.sexo,
  sexoCode: normalizeSexoCode(row.sexo),
  estado: row.estado,
  municipio: row.municipio,
  parroquia: row.parroquia,
  comunidad: row.comunidad,
  centro: row.centro,
  jornada:
    row.nombre_jornada || `${MEDICAL_DEPLOYMENT_LABELS.singularShort} ${row.id_convocatoria}`,
  motivoConsulta: row.motivoConsulta1,
  diagnostico: row.diagnosticos1,
  tipoEnfermedad: row.tipoEnfermedad || row.tipoEnfermedadDescripcion || row.tipo_enfermedad1 || "Sin dato",
  medico: row.medico,
  medicoId: row.medicoId,
  estadoAtencion: row.estado_atencion_label || "Atendido",
  estadoAtencionRaw: row.estado_atencion_raw,
  estadoAtencionValue: row.estado_atencion_raw || "atendido",
  discapacidad: hasDiscapacidad(row.discapacidad),
  discapacidadRaw: row.discapacidad,
  riesgo:
    hasFactorRiesgo(row.factores_riesgo1) ||
    hasFactorRiesgo(row.factores_riesgo2) ||
    hasFactorRiesgo(row.factores_riesgo3),
  riesgoRaw: {
    factores_riesgo1: row.factores_riesgo1,
    factores_riesgo2: row.factores_riesgo2,
    factores_riesgo3: row.factores_riesgo3,
  },
  rangoEdad: row.rango_edad,
  edadYears: parseAgeYears(row.edad),
  codigoProyecto: row.codigo_proyecto,
  nombreProyecto: row.nombre_proyecto,
  codigoProyectoVisual: row.codigo_proyecto_visual,
  idConvocatoria: row.id_convocatoria,
  patientKey: row.patient_key,
  patientKeyConfidence: row.patient_key_confidence,
  totalAtencionesPersona: Number(row.total_atenciones_persona || 1),
  numeroAtencionPersona: Number(row.numero_atencion_persona || 1),
  esReconsulta: toBooleanFlag(row.es_reconsulta),
  fechaPrimeraAtencion: row.fecha_primera_atencion,
  fechaUltimaAtencion: row.fecha_ultima_atencion,
});

const fetchDashboardData = async (filters = {}) => {
  const params = {};
  // const TEMP_CODIGO_PROYECTO = "42";

  if (filters.codigoProyecto) params.codigo_proyecto = filters.codigoProyecto;
  if (filters.idConvocatoria) params.id_convocatoria = filters.idConvocatoria;
  if (filters.fechaDesde) params.fecha_desde = filters.fechaDesde;
  if (filters.fechaHasta) params.fecha_hasta = filters.fechaHasta;
  if (filters.estado) params.estado = filters.estado;
  if (filters.municipio) params.municipio = filters.municipio;
  if (filters.parroquia) params.parroquia = filters.parroquia;
  if (filters.comunidad) params.comunidad = filters.comunidad;
  if (filters.centro) params.centro = filters.centro;
  if (filters.sexo) params.sexo = filters.sexo;
  if (filters.rangoEdad) params.rango_edad = filters.rangoEdad;
  if (filters.medicoId) params.medicoId = filters.medicoId;
  if (filters.medico) params.medico = filters.medico;
  if (filters.estadoAtencion) params.estado_atencion = filters.estadoAtencion;
  if (filters.busqueda) params.busqueda = filters.busqueda;
  if (filters.quickFilter === "reconsultas") params.es_reconsulta = true;

  const response = await axios.get("/dashboard-salud-emergencia", { params });
  const payload = response?.data?.data || response?.data || {};
  const requiresProjectFilter = payload.meta?.requiresProjectFilter === true;
  const rows = Array.isArray(payload.tabla) ? payload.tabla : [];

  if (!payload || (!requiresProjectFilter && !Array.isArray(payload.tabla))) {
    throw new Error("Respuesta inválida del dashboard Salud Emergencia");
  }

  return {
    ...payload,
    tabla: rows.map(normalizeBackendRow),
  };
};

const formatDate = (date) => {
  if (!date || typeof date !== "string" || !date.includes("-")) return date || "Sin fecha";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
};

const sortDateAsc = (a, b) => String(a).localeCompare(String(b));

const formatDateTime = (date) =>
  new Intl.DateTimeFormat("es-VE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getOptionId = (option) =>
  typeof option === "string"
    ? option
    : option?.id ??
      option?.id_estado ??
      option?.idEstado ??
      option?.idestado ??
      option?.idestados ??
      option?.id_municipio ??
      option?.idMunicipio ??
      option?.idmunicipio ??
      option?.id_municipios ??
      option?.id_parroquia ??
      option?.idParroquia ??
      option?.idparroquia ??
      option?.id_parroquias ??
      option?.value ??
      "";

const getOptionName = (option) =>
  typeof option === "string"
    ? option
    : option?.nombre ??
      option?.name ??
      option?.estado ??
      option?.Estado ??
      option?.municipio ??
      option?.Municipio ??
      option?.parroquia ??
      option?.Parroquia ??
      option?.nombre_estado ??
      option?.nombreEstado ??
      option?.nombre_municipio ??
      option?.nombreMunicipio ??
      option?.nombre_parroquia ??
      option?.nombreParroquia ??
      option?.descripcion ??
      option?.label ??
      "";

const normalizeOptions = (items = []) =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          ...item,
          optionId: String(getOptionId(item) || ""),
          optionName: String(getOptionName(item) || ""),
        }))
        .filter((item) => item.optionId || item.optionName)
    : [];

const toBooleanFlag = (value) => {
  const normalized = normalizeText(value);

  return value === true || normalized === "1" || normalized === "si" || normalized === "sí" || normalized === "true";
};

const hasDiscapacidad = (value) => {
  if (value === null || value === undefined || value === "") return false;

  const numericValue = Number(value);

  if ([1, 2, 3, 4].includes(numericValue)) return true;
  if ([0, 5, 6].includes(numericValue)) return false;

  const normalized = normalizeText(value);

  if (["fisica", "intelectual", "sensorial", "multiple"].includes(normalized)) return true;
  if (["ninguna", "no aplica", "no", "sin discapacidad"].includes(normalized)) return false;

  return false;
};

const hasFactorRiesgo = (value) => {
  if (value === null || value === undefined || value === "") return false;

  const numericValue = Number(value);

  if (numericValue >= 1 && numericValue <= 11) return true;
  if ([0, 12].includes(numericValue)) return false;

  const normalized = normalizeText(value);

  if (["no aplica", "ninguno", "ninguna", "no", "sin riesgo", "sin factor", "sin factores"].includes(normalized)) {
    return false;
  }

  return Boolean(normalized);
};

const normalizeSexoCode = (value = "") => {
  const normalized = normalizeText(value);
  if (["f", "femenino", "mujer", "fem", "female"].includes(normalized)) return "F";
  if (["m", "masculino", "hombre", "masc", "male"].includes(normalized)) return "M";
  return "";
};

const parseAgeYears = (value) => {
  if (value === null || value === undefined) return null;
  const match = String(value).match(/\d+/);
  return match ? Number(match[0]) : null;
};

const sameNormalized = (a, b) => normalizeText(a) === normalizeText(b);

const toISODate = (date) => date.toISOString().slice(0, 10);

const getTodayISO = () => toISODate(new Date());

const subtractDaysISO = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toISODate(date);
};

const hasArrayData = (items) => Array.isArray(items) && items.length > 0;

const hasLineData = (lineData) =>
  Array.isArray(lineData) &&
  lineData.length > 0 &&
  Array.isArray(lineData[0].data) &&
  lineData[0].data.length > 0 &&
  lineData[0].data.every((point) => point.x && point.y !== null && point.y !== undefined);

const hasBarData = (items, keys = ["total"]) =>
  hasArrayData(items) &&
  items.some((item) => keys.some((key) => Number(item[key]) > 0)) &&
  items.every((item) => keys.every((key) => item[key] !== null && item[key] !== undefined));

const hasPieData = (items) =>
  hasArrayData(items) &&
  items.some((item) => Number(item.value) > 0) &&
  items.every((item) => item.id && item.value !== null && item.value !== undefined);

const countBy = (items, key) =>
  items.reduce((acc, item) => {
    const value = item[key] || "Sin dato";
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

const toSelectOptions = (items, key) =>
  Object.keys(countBy(items, key))
    .filter(Boolean)
    .sort();

const getAgeRange = (age) => {
  if (age <= 5) return "0-5";
  if (age <= 17) return "6-17";
  if (age <= 49) return "18-49";
  return "50+";
};

const formatNumeroAtencion = (numero = 1) => {
  if (numero === 1) return "1ra atención";
  if (numero === 2) return "2da atención";
  if (numero === 3) return "3ra atención";
  return `${numero}ª atención`;
};

const normalizeMockRow = (row, index) => {
  const esReconsulta = index === 7 || index === 10;

  return {
    ...row,
    patientKey: row.codigo,
    patientKeyConfidence: "mock",
    sexoOriginal: row.sexo,
    sexoCode: normalizeSexoCode(row.sexo),
    rangoEdad: getAgeRange(row.edad),
    edadYears: parseAgeYears(row.edad),
    estadoAtencionValue: row.estadoAtencion || "atendido",
    totalAtencionesPersona: esReconsulta ? 2 : 1,
    numeroAtencionPersona: esReconsulta ? 2 : 1,
    esReconsulta,
    fechaPrimeraAtencion: row.fecha,
    fechaUltimaAtencion: row.fecha,
  };
};

const toChartData = (items, key, indexKey = "id") =>
  Object.entries(countBy(items, key)).map(([label, total]) => ({
    [indexKey]: label,
    total,
  }));

const getValueFromChartItem = (item) => {
  return Number(
    item.total ||
      item.cantidad ||
      item.value ||
      item.atenciones ||
      item.personas ||
      item.count ||
      0
  );
};

const limitChartRanking = (items = [], limit = 10, withOthers = true) => {
  if (!Array.isArray(items)) return [];

  const sortedItems = [...items].sort((a, b) => getValueFromChartItem(b) - getValueFromChartItem(a));

  if (sortedItems.length <= limit) {
    return sortedItems;
  }

  const topItems = sortedItems.slice(0, limit);

  if (!withOthers) {
    return topItems;
  }

  const otherTotal = sortedItems
    .slice(limit)
    .reduce((sum, item) => sum + getValueFromChartItem(item), 0);

  if (otherTotal <= 0) {
    return topItems;
  }

  return [
    ...topItems,
    {
      id: "Otros",
      name: "Otros",
      label: "Otros",
      diagnostico: "Otros",
      comunidad: "Otros",
      municipio: "Otros",
      total: otherTotal,
      cantidad: otherTotal,
      value: otherTotal,
    },
  ];
};

const EmptyChartState = () => (
  <Box height="100%" display="flex" alignItems="center" justifyContent="center" textAlign="center" px={2}>
    <Typography variant="body2" color="text.secondary">
      No hay datos suficientes para este gráfico.
    </Typography>
  </Box>
);

const SectionTitle = ({ title, pill, icon }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
    <Typography variant="h5" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
      {icon}
      {title}
    </Typography>
    {pill && (
      <Chip
        label={pill}
        size="small"
        sx={{ bgcolor: "rgba(0, 159, 227, 0.12)", color: "#004f7c", fontWeight: 800 }}
      />
    )}
  </Box>
);

const StatCard = ({ title, value, icon, caption, color = "#008080" }) => {
  const theme = useTheme();

  return (
    <DashboardCard>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            borderRadius: "18px",
            bgcolor: color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="h3" sx={{ color: theme.palette.secondary[100], fontWeight: 900 }}>
            <CountUp end={value} separator="." />
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200], fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: "#0f766e", fontWeight: 800 }}>
            {caption}
          </Typography>
        </Box>
      </Box>
    </DashboardCard>
  );
};

const DashboardSaludEmergencia = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [mapPoints, setMapPoints] = useState([]);
  const [dashboardResponse, setDashboardResponse] = useState({
    kpis: {},
    charts: {},
    filters: {},
    meta: {},
  });
  const [estadoOptions, setEstadoOptions] = useState([]);
  const [municipioOptions, setMunicipioOptions] = useState([]);
  const [parroquiaOptions, setParroquiaOptions] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchDashboardData(filters);
      const needsProjectSelection = response.meta?.requiresProjectFilter === true;
      setData(needsProjectSelection ? [] : response.tabla || []);
      setDashboardResponse({
        kpis: response.kpis || {},
        charts: response.charts || {},
        filters: response.filters || {},
        meta: response.meta || {},
      });
      setMapPoints(
        !needsProjectSelection && Array.isArray(response.mapa?.jornadas) ? response.mapa.jornadas : []
      );
      setLastUpdated(new Date());
    } catch (err) {
      setError("No se pudo actualizar el panel. Se mantienen los datos mock disponibles.");
      setData(mockData.map(normalizeMockRow));
      setMapPoints([]);
      setDashboardResponse({
        kpis: {},
        charts: {},
        filters: {},
        meta: {},
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadData();
      }
    }, 180000);

    return () => clearInterval(intervalId);
  }, [loadData]);

  const loadEstados = useCallback(async () => {
    try {
      const response = await axios.get("/getEstados/");
      setEstadoOptions(normalizeOptions(response.data));
    } catch (err) {
      setEstadoOptions([]);
    }
  }, []);

  const loadMunicipios = useCallback(async (estadoId) => {
    if (!estadoId) {
      setMunicipioOptions([]);
      return;
    }

    try {
      const response = await axios.get("/getMunicipios/", { params: { id: estadoId } });
      setMunicipioOptions(normalizeOptions(response.data));
    } catch (err) {
      setMunicipioOptions([]);
    }
  }, []);

  const loadParroquias = useCallback(async (municipioId) => {
    if (!municipioId) {
      setParroquiaOptions([]);
      return;
    }

    try {
      const response = await axios.get("/getParroquias/", { params: { id: municipioId } });
      setParroquiaOptions(normalizeOptions(response.data));
    } catch (err) {
      setParroquiaOptions([]);
    }
  }, []);

  useEffect(() => {
    loadEstados();
  }, [loadEstados]);

  useEffect(() => {
    if (!filters.estadoId) {
      setMunicipioOptions([]);
      return;
    }
    loadMunicipios(filters.estadoId);
  }, [filters.estadoId, loadMunicipios]);

  useEffect(() => {
    if (!filters.municipioId) {
      setParroquiaOptions([]);
      return;
    }
    loadParroquias(filters.municipioId);
  }, [filters.municipioId, loadParroquias]);

  useEffect(() => {
    if (!filters.estado || filters.estadoId || !estadoOptions.length) return;

    const selected = estadoOptions.find((option) =>
      sameNormalized(option.optionName || getOptionName(option), filters.estado)
    );

    if (!selected) return;

    setFilters((current) => ({
      ...current,
      estadoId: selected.optionId || getOptionId(selected),
    }));
  }, [estadoOptions, filters.estado, filters.estadoId]);

  useEffect(() => {
    if (!filters.municipio || filters.municipioId || !municipioOptions.length) return;

    const selected = municipioOptions.find((option) =>
      sameNormalized(option.optionName || getOptionName(option), filters.municipio)
    );

    if (!selected) return;

    setFilters((current) => ({
      ...current,
      municipioId: selected.optionId || getOptionId(selected),
    }));
  }, [municipioOptions, filters.municipio, filters.municipioId]);

  useEffect(() => {
    if (!filters.parroquia || filters.parroquiaId || !parroquiaOptions.length) return;

    const selected = parroquiaOptions.find((option) =>
      sameNormalized(option.optionName || getOptionName(option), filters.parroquia)
    );

    if (!selected) return;

    setFilters((current) => ({
      ...current,
      parroquiaId: selected.optionId || getOptionId(selected),
    }));
  }, [parroquiaOptions, filters.parroquia, filters.parroquiaId]);

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleEstadoChange = (event) => {
    const selectedValue = event.target.value;
    const selectedEstado = estadoOptions.find(
      (option) => String(option.optionId || option.optionName) === String(selectedValue)
    );
    const estadoId = selectedEstado?.optionId || "";
    const estadoName = selectedValue ? selectedEstado?.optionName || String(selectedValue) : "";

    setFilters((current) => ({
      ...current,
      estadoId,
      estado: estadoName,
      municipioId: "",
      municipio: "",
      parroquiaId: "",
      parroquia: "",
    }));
    setMunicipioOptions([]);
    setParroquiaOptions([]);
  };

  const handleMunicipioChange = (event) => {
    const selectedValue = event.target.value;
    const selectedMunicipio = municipioOptions.find(
      (option) => String(option.optionId || option.optionName) === String(selectedValue)
    );
    const municipioId = selectedMunicipio?.optionId || "";
    const municipioName = selectedValue ? selectedMunicipio?.optionName || String(selectedValue) : "";

    setFilters((current) => ({
      ...current,
      municipioId,
      municipio: municipioName,
      parroquiaId: "",
      parroquia: "",
    }));
    setParroquiaOptions([]);
  };

  const handleParroquiaChange = (event) => {
    const selectedValue = event.target.value;
    const selectedParroquia = parroquiaOptions.find(
      (option) => String(option.optionId || option.optionName) === String(selectedValue)
    );
    const parroquiaId = selectedParroquia?.optionId || "";
    const parroquiaName = selectedValue ? selectedParroquia?.optionName || String(selectedValue) : "";

    setFilters((current) => ({
      ...current,
      parroquiaId,
      parroquia: parroquiaName,
    }));
  };

  const handleQuickFilter = (key) => {
    setFilters((current) => ({
      ...current,
      quickFilter: current.quickFilter === key ? "" : key,
      busqueda: key === "buscar" ? current.busqueda : current.busqueda,
    }));
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const searchValue = normalizeText(filters.busqueda);
      const searchableText = normalizeText(
        [
          item.beneficiario,
          item.codigo,
          item.comunidad,
          item.centro,
          item.jornada,
          item.motivoConsulta,
          item.diagnostico,
          item.tipoEnfermedad,
          item.medico,
          item.estadoAtencion,
          item.esReconsulta ? "reconsulta" : "",
        ].join(" ")
      );

      const matchesBaseFilters =
        (!filters.fechaDesde || item.fecha >= filters.fechaDesde) &&
        (!filters.fechaHasta || item.fecha <= filters.fechaHasta) &&
        (!filters.estado || sameNormalized(item.estado, filters.estado)) &&
        (!filters.municipio || sameNormalized(item.municipio, filters.municipio)) &&
        (!filters.parroquia || sameNormalized(item.parroquia, filters.parroquia)) &&
        (!filters.comunidad || sameNormalized(item.comunidad, filters.comunidad)) &&
        (!filters.centro || sameNormalized(item.centro, filters.centro)) &&
        (!filters.jornada || sameNormalized(item.jornada, filters.jornada)) &&
        (!filters.sexo || item.sexoCode === filters.sexo) &&
        (!filters.rangoEdad || sameNormalized(item.rangoEdad, filters.rangoEdad)) &&
        (!filters.medico || sameNormalized(item.medico, filters.medico)) &&
        (!filters.estadoAtencion ||
          sameNormalized(item.estadoAtencionValue, filters.estadoAtencion) ||
          sameNormalized(item.estadoAtencion, filters.estadoAtencion)) &&
        (!searchValue || searchableText.includes(searchValue));

      if (!matchesBaseFilters) return false;

      if (filters.quickFilter === "today") return item.fecha === getTodayISO();
      if (filters.quickFilter === "7days") return item.fecha >= subtractDaysISO(7);
      if (filters.quickFilter === "30days") return item.fecha >= subtractDaysISO(30);
      if (filters.quickFilter === "referidos") return item.estadoAtencion === "Referido";
      if (filters.quickFilter === "reconsultas") return item.esReconsulta === true;
      if (filters.quickFilter === "discapacidad") return item.discapacidad === true;
      if (filters.quickFilter === "riesgo") return item.riesgo === true;
      if (filters.quickFilter === "adultos") return Number(item.edadYears || 0) >= 55;

      return true;
    });
  }, [data, filters]);

  const filtersSignature = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    setPage(0);
  }, [filtersSignature]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredData.length, page, rowsPerPage]);

  const paginatedTableRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const totalTableRows = filteredData.length;
  const fromTableRow = totalTableRows === 0 ? 0 : page * rowsPerPage + 1;
  const toTableRow = Math.min((page + 1) * rowsPerPage, totalTableRows);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const requiresProjectFilter = dashboardResponse.meta?.requiresProjectFilter === true;
  const useBackendVisuals = !filters.quickFilter || filters.quickFilter === "reconsultas";

  const kpis = useMemo(() => {
    const backendKpis = dashboardResponse.kpis || {};
    const canUseBackendKpis = Object.keys(backendKpis).length > 0 && useBackendVisuals;

    if (canUseBackendKpis) {
      return {
        personas: Number(backendKpis.personasAtendidas) || 0,
        atenciones: Number(backendKpis.atencionesSalud) || 0,
        comunidades: Number(backendKpis.comunidadesAtendidas) || 0,
        referidos: Number(backendKpis.referidos) || 0,
        personasReatendidas: Number(backendKpis.personasReatendidas) || 0,
        atencionesRepetidas: Number(backendKpis.atencionesRepetidas) || 0,
        medicos: Number(backendKpis.medicosActivos) || 0,
        jornadas: Number(backendKpis.jornadas) || 0,
      };
    }

    const referidos = filteredData.filter((item) => sameNormalized(item.estadoAtencion, "Referido")).length;
    const personasReatendidas = new Set(
      filteredData
        .filter((item) => item.esReconsulta || item.totalAtencionesPersona > 1)
        .map((item) => item.patientKey || item.codigo)
    ).size;
    const atencionesRepetidas = filteredData.filter((item) => item.esReconsulta).length;

    return {
      personas: new Set(filteredData.map((item) => item.patientKey || item.codigo)).size,
      atenciones: filteredData.length,
      comunidades: Object.keys(countBy(filteredData, "comunidad")).length,
      referidos,
      personasReatendidas,
      atencionesRepetidas,
      medicos: Object.keys(countBy(filteredData, "medico")).length,
      jornadas: Object.keys(countBy(filteredData, "jornada")).length,
    };
  }, [dashboardResponse.kpis, filteredData, useBackendVisuals]);

  const lineData = useMemo(() => {
    const source = useBackendVisuals ? dashboardResponse.charts?.atencionesPorDia : null;

    if (Array.isArray(source) && source.length > 0) {
      const sortedSource = [...source]
        .filter((item) => item.fecha)
        .sort((a, b) => sortDateAsc(a.fecha, b.fecha));

      return [
        {
          id: "Atenciones",
          color: "#009fe3",
          data: sortedSource.map((item) => ({
            x: formatDate(item.fecha),
            y: Number(item.total) || 0,
          })),
        },
      ];
    }

    const byDay = Object.entries(countBy(filteredData, "fecha"))
      .filter(([fecha]) => fecha && fecha !== "Sin dato")
      .sort(([a], [b]) => sortDateAsc(a, b))
      .map(([fecha, total]) => ({ x: formatDate(fecha), y: Number(total) || 0 }));

    return [{ id: "Atenciones", color: "#009fe3", data: byDay }];
  }, [useBackendVisuals, dashboardResponse.charts, filteredData]);

  const communityData = useMemo(() => {
    const source = useBackendVisuals ? dashboardResponse.charts?.atencionesPorComunidad : null;

    if (Array.isArray(source) && source.length > 0) {
      return source.map((item) => ({
        comunidad: item.comunidad || item.municipio || item.label || item.id || "Sin dato",
        total: Number(item.total) || 0,
      }));
    }

    return toChartData(filteredData, "comunidad", "comunidad");
  }, [useBackendVisuals, dashboardResponse.charts, filteredData]);
  const communityChartData = useMemo(() => limitChartRanking(communityData, 10, true), [communityData]);
  const diagnosisData = useMemo(() => toChartData(filteredData, "diagnostico"), [filteredData]);
  const diagnosisPieData = useMemo(
    () => {
      const source = useBackendVisuals ? dashboardResponse.charts?.diagnosticos : null;

      if (Array.isArray(source) && source.length > 0) {
        return source
          .map((item) => ({
            id: item.diagnostico || item.label || item.id || "Sin dato",
            label: item.diagnostico || item.label || item.id || "Sin dato",
            value: Number(item.total) || 0,
          }))
          .filter((item) => item.id && item.value > 0);
      }

      return diagnosisData
        .map((item) => ({ id: item.id, label: item.id, value: Number(item.total) || 0 }))
        .filter((item) => item.id && item.value > 0);
    },
    [useBackendVisuals, dashboardResponse.charts, diagnosisData]
  );
  const diagnosisPieChartData = useMemo(() => limitChartRanking(diagnosisPieData, 20, true), [diagnosisPieData]);
  const statusData = useMemo(
    () => {
      const source = useBackendVisuals ? dashboardResponse.charts?.estadoAtencion : null;

      if (Array.isArray(source) && source.length > 0) {
        return source
          .map((item) => ({
            id: item.estado_atencion_label || item.estadoAtencion || item.label || item.id || "Sin dato",
            label: item.estado_atencion_label || item.estadoAtencion || item.label || item.id || "Sin dato",
            value: Number(item.total ?? item.value) || 0,
          }))
          .filter((item) => item.id && item.value > 0);
      }

      return Object.entries(countBy(filteredData, "estadoAtencion")).map(([label, value]) => ({
        id: label,
        label,
        value: Number(value) || 0,
      }));
    },
    [useBackendVisuals, dashboardResponse.charts, filteredData]
  );
  const sexAgeData = useMemo(() => {
    const source = useBackendVisuals ? dashboardResponse.charts?.sexoEdad : null;

    if (Array.isArray(source) && source.length > 0) {
      return source
        .map((item) => ({
          grupo: item.rangoEdad || item.rango_edad || item.grupo || item.label || "Sin rango",
          Mujeres: Number(item.F ?? item.f ?? item.Mujeres ?? item.mujeres ?? 0) || 0,
          Hombres: Number(item.M ?? item.m ?? item.Hombres ?? item.hombres ?? 0) || 0,
          SinDato: Number(item.SinDato ?? item.sinDato ?? item.sin_dato ?? 0) || 0,
        }))
        .filter((item) => item.grupo && (item.Mujeres > 0 || item.Hombres > 0 || item.SinDato > 0));
    }

    const ranges = toSelectOptions(filteredData, "rangoEdad");

    const groups = ranges.map((range) => ({
      grupo: range,
      Mujeres: filteredData.filter((item) => item.sexoCode === "F" && sameNormalized(item.rangoEdad, range)).length,
      Hombres: filteredData.filter((item) => item.sexoCode === "M" && sameNormalized(item.rangoEdad, range)).length,
      SinDato: filteredData.filter((item) => !item.sexoCode && sameNormalized(item.rangoEdad, range)).length,
    })).filter((item) => item.Mujeres > 0 || item.Hombres > 0 || item.SinDato > 0);

    return groups;
  }, [useBackendVisuals, dashboardResponse.charts, filteredData]);
  const sexAgeKeys = useMemo(() => {
    const hasSinDato = sexAgeData.some((item) => Number(item.SinDato) > 0);
    return hasSinDato ? ["Mujeres", "Hombres", "SinDato"] : ["Mujeres", "Hombres"];
  }, [sexAgeData]);
  const rangoEdadOptions = useMemo(() => {
    const fromChart = dashboardResponse.charts?.sexoEdad?.map((item) => item.rangoEdad) || [];
    const fromData = toSelectOptions(data, "rangoEdad");
    return Array.from(new Set([...fromChart, ...fromData].filter(Boolean)));
  }, [dashboardResponse.charts, data]);
  const backendFilters = dashboardResponse.filters || {};
  const proyectoOptions =
    Array.isArray(backendFilters.proyectos) && backendFilters.proyectos.length ? backendFilters.proyectos : [];
  const comunidadOptions =
    Array.isArray(backendFilters.comunidades) && backendFilters.comunidades.length
      ? backendFilters.comunidades
      : toSelectOptions(data, "comunidad");
  const centroOptions =
    Array.isArray(backendFilters.centros) && backendFilters.centros.length
      ? backendFilters.centros
      : toSelectOptions(data, "centro");
  const medicoOptions =
    Array.isArray(backendFilters.medicos) && backendFilters.medicos.length
      ? backendFilters.medicos
      : toSelectOptions(data, "medico").map((medico) => ({ medico, medicoId: "" }));
  const estadoAtencionOptions =
    Array.isArray(backendFilters.estadosAtencion) && backendFilters.estadosAtencion.length
      ? backendFilters.estadosAtencion
      : toSelectOptions(data, "estadoAtencion").map((label) => ({ value: label, label }));
  const jornadaOptions =
    Array.isArray(backendFilters.jornadas) && backendFilters.jornadas.length
      ? backendFilters.jornadas
      : toSelectOptions(data, "jornada").map((nombre_jornada) => ({
          id_convocatoria: "",
          nombre_jornada,
        }));

  const handleProyectoChange = (event) => {
    const selectedValue = event.target.value;

    setFilters((current) => ({
      ...current,
      codigoProyecto: selectedValue,
      idConvocatoria: "",
      jornada: "",
    }));
  };

  const handleMedicoChange = (event) => {
    const selectedValue = event.target.value;
    const selectedMedico = medicoOptions.find(
      (option) =>
        String(option.medicoId || option.id || option.value || option.medico || option.nombre || "") ===
        String(selectedValue)
    );

    setFilters((current) => ({
      ...current,
      medicoId: selectedMedico?.medicoId || selectedMedico?.id || "",
      medico: selectedMedico?.medico || selectedMedico?.nombre || selectedValue,
    }));
  };

  const handleJornadaChange = (event) => {
    const selectedValue = event.target.value;
    const selectedJornada = jornadaOptions.find(
      (option) =>
        String(option.id_convocatoria || option.value || option.nombre_jornada || option.label || "") ===
        String(selectedValue)
    );

    setFilters((current) => ({
      ...current,
      idConvocatoria: selectedJornada?.id_convocatoria || selectedJornada?.id || "",
      jornada: selectedJornada?.nombre_jornada || selectedJornada?.label || selectedValue,
    }));
  };

  const exportReport = () => {
    const headers = [
      "Fecha",
      "Código",
      "Beneficiario",
      "Edad",
      "Sexo",
      "Comunidad",
      "Centro",
      MEDICAL_DEPLOYMENT_LABELS.singularShort,
      "Motivo consulta",
      "Diagnóstico",
      "Tipo enfermedad",
      "Médico",
      "Atención",
      "Estado atención",
    ];
    const rows = filteredData.map((row) =>
      [
        formatDate(row.fecha),
        row.codigo,
        row.beneficiario,
        row.edad,
        row.sexo,
        row.comunidad,
        row.centro,
        row.jornada,
        row.motivoConsulta,
        row.diagnostico,
        row.tipoEnfermedad,
        row.medico,
        formatNumeroAtencion(row.numeroAtencionPersona),
        row.estadoAtencion,
      ]
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dashboard-salud-emergencia.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const nivoTheme = {
    axis: {
      ticks: { text: { fill: theme.palette.secondary[200] } },
      legend: { text: { fill: theme.palette.secondary[200] } },
    },
    legends: { text: { fill: theme.palette.secondary[200] } },
    labels: { text: { fill: theme.palette.secondary[100] } },
    tooltip: { container: { color: theme.palette.primary.main } },
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.5}>
            <Chip
              label="Dashboard especial · solo salud"
              size="small"
              sx={{
                alignSelf: "flex-start",
                bgcolor: "rgba(0, 159, 227, 0.12)",
                color: "#004f7c",
                fontWeight: 900,
              }}
            />
            <Typography variant={isSmallScreen ? "h3" : "h2"} sx={{ fontWeight: 900, lineHeight: 1.05 }}>
              Panel de Salud · Emergencia
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 800 }}>
              {filters.estado ? `Estado: ${filters.estado}` : "Todos los estados disponibles"}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Visualización de atenciones de salud por despliegue de unidad médica móvil, proyecto y socio
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <DashboardCard>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
              <Typography variant="h5" sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#22a06b",
                    boxShadow: "0 0 0 5px rgba(34,160,107,.14)",
                  }}
                />
                Datos en vivo
              </Typography>
              <Chip label="Auto · cada 3 min" size="small" sx={{ bgcolor: "rgba(0,128,128,.12)", fontWeight: 900 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Última actualización: <strong>{lastUpdated ? formatDateTime(lastUpdated) : "Sin actualizar"}</strong>
            </Typography>
            <Stack direction={isMediumScreen ? "column" : "row"} spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshOutlinedIcon />}
                onClick={loadData}
                disabled={loading}
                sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#006c6c" } }}
              >
                Actualizar ahora
              </Button>
              <Button variant="outlined" startIcon={<CloudDownloadOutlinedIcon />} onClick={exportReport}>
                Exportar reporte
              </Button>
            </Stack>
          </DashboardCard>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="warning">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <DashboardCard>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
              <Typography variant="h4" sx={{ fontWeight: 900, display: "flex", alignItems: "center" }}>
                <TuneOutlinedIcon sx={{ mr: 1 }} />
                Filtros avanzados
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                Vista pensada para socios · filtros configurables
              </Typography>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    label="Fecha desde"
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(event) => handleFilterChange("fechaDesde", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    label="Fecha hasta"
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(event) => handleFilterChange("fechaHasta", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Proyecto"
                    value={filters.codigoProyecto}
                    onChange={handleProyectoChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {proyectoOptions.map((option) => {
                      const value = String(option.codigo_proyecto || option.codigoProyecto || option.value || "");
                      const label =
                        option.nombre_proyecto || option.nombreProyecto || option.label || value || "Proyecto";
                      const visual = option.codigo_proyecto_visual || option.codigoProyectoVisual || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {visual ? `${label} · ${visual}` : label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Estado"
                    value={filters.estadoId}
                    onChange={handleEstadoChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {estadoOptions.map((option) => {
                      return (
                        <MenuItem key={option.optionId || option.optionName} value={option.optionId || option.optionName}>
                          {option.optionName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Municipio"
                    value={filters.municipioId}
                    onChange={handleMunicipioChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {municipioOptions.map((option) => {
                      return (
                        <MenuItem key={option.optionId || option.optionName} value={option.optionId || option.optionName}>
                          {option.optionName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Parroquia"
                    value={filters.parroquiaId}
                    onChange={handleParroquiaChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {parroquiaOptions.map((option) => {
                      return (
                        <MenuItem key={option.optionId || option.optionName} value={option.optionId || option.optionName}>
                          {option.optionName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Comunidad"
                    value={filters.comunidad}
                    onChange={(event) => handleFilterChange("comunidad", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {comunidadOptions.map((option) => {
                      const value = typeof option === "string" ? option : option.comunidad || option.nombre || option.value || "";
                      const label = typeof option === "string" ? option : option.label || option.comunidad || option.nombre || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Centro de atención"
                    value={filters.centro}
                    onChange={(event) => handleFilterChange("centro", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {centroOptions.map((option) => {
                      const value = typeof option === "string" ? option : option.centro || option.nombre || option.value || "";
                      const label = typeof option === "string" ? option : option.label || option.centro || option.nombre || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Despliegue / Convocatoria"
                    value={filters.idConvocatoria || filters.jornada}
                    onChange={handleJornadaChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {jornadaOptions.map((option) => {
                      const value = String(option.id_convocatoria || option.value || option.nombre_jornada || option.label || "");
                      const label = option.nombre_jornada || option.label || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Sexo"
                    value={filters.sexo}
                    onChange={(event) => handleFilterChange("sexo", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="F">Femenino</MenuItem>
                    <MenuItem value="M">Masculino</MenuItem>
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Rango de edad"
                    value={filters.rangoEdad}
                    onChange={(event) => handleFilterChange("rangoEdad", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {rangoEdadOptions.map((range) => (
                      <MenuItem key={range} value={range}>
                        {range}
                      </MenuItem>
                    ))}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Médico"
                    value={filters.medicoId || filters.medico}
                    onChange={handleMedicoChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {medicoOptions.map((option) => {
                      const value = option.medicoId || option.id || option.value || option.medico || option.nombre || "";
                      const label = option.medico || option.nombre || option.label || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Estado atención"
                    value={filters.estadoAtencion}
                    onChange={(event) => handleFilterChange("estadoAtencion", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {estadoAtencionOptions.map((option) => {
                      const value = typeof option === "string" ? option : option.value || option.estado_atencion || option.label || "";
                      const label = typeof option === "string" ? option : option.label || option.estado_atencion || option.value || value;

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
            </Grid>

            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, mt: 2 }}>
              {quickFilters.map((filter) => (
                <Chip
                  key={filter.key}
                  label={filter.label}
                  onClick={() => handleQuickFilter(filter.key)}
                  color={filters.quickFilter === filter.key ? "primary" : "default"}
                  variant={filters.quickFilter === filter.key ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 800,
                    bgcolor:
                      filters.quickFilter === filter.key
                        ? "#009fe3"
                        : filter.key === "referidos" ||
                          filter.key === "reconsultas" ||
                          filter.key === "discapacidad" ||
                          filter.key === "riesgo" ||
                          filter.key === "adultos"
                        ? "rgba(0,128,128,.10)"
                        : undefined,
                  }}
                />
              ))}
            </Stack>
            <TextField
              label="Buscar beneficiario"
              value={filters.busqueda}
              onChange={(event) => handleFilterChange("busqueda", event.target.value)}
              placeholder="Nombre o código"
              size="small"
              sx={{ mt: 2, maxWidth: 360 }}
              fullWidth
              InputProps={{
                startAdornment: <PersonSearchOutlinedIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />
          </DashboardCard>
        </Grid>

        {loading ? (
          <Grid item xs={12}>
            <DashboardCard>
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                <CircularProgress />
              </Box>
            </DashboardCard>
          </Grid>
        ) : requiresProjectFilter ? (
          <Grid item xs={12}>
            <DashboardCard>
              <Alert severity="info" sx={{ alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {dashboardResponse.meta?.message || "Seleccione un proyecto para consultar el dashboard."}
                </Typography>
                <Typography variant="body2">
                  Para evitar cargar todo el histórico, el dashboard requiere seleccionar un proyecto antes de mostrar
                  los indicadores.
                </Typography>
              </Alert>
            </DashboardCard>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Personas atendidas"
                value={kpis.personas}
                caption="+ registros filtrados"
                color="#009fe3"
                icon={<GroupsOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Atenciones de salud"
                value={kpis.atenciones}
                caption="100% salud"
                color="#008080"
                icon={<LocalHospitalOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Comunidades atendidas"
                value={kpis.comunidades}
                caption="Cobertura territorial"
                color="#22a06b"
                icon={<PlaceOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Reconsultas"
                value={kpis.personasReatendidas}
                caption={`${kpis.atencionesRepetidas} atenciones repetidas`}
                color="#f39313"
                icon={<WarningAmberOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Médicos activos"
                value={kpis.medicos}
                caption="Equipo clínico"
                color="#004f7c"
                icon={<MedicalServicesOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <StatCard
                title="Despliegues realizados"
                value={kpis.jornadas}
                caption="Convocatorias"
                color="#d94841"
                icon={<CalendarMonthOutlinedIcon sx={{ color: "white" }} />}
              />
            </Grid>

            <Grid item xs={12}>
              <DashboardCard>
                <SectionTitle
                  title="Mapa de despliegues realizados"
                  pill={`${mapPoints.length} puntos con coordenadas`}
                  icon={<PlaceOutlinedIcon sx={{ mr: 1 }} />}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ubicación geográfica de los despliegues según los filtros aplicados.
                </Typography>
                <JornadasMap points={mapPoints} height={420} />
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <DashboardCard>
                <SectionTitle title="Atenciones por día" pill="Tendencia semanal" />
                <Box height={300}>
                  {hasLineData(lineData) ? (
                    <ResponsiveLine
                      data={lineData}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 25, bottom: 55, left: 45 }}
                      xScale={{ type: "point" }}
                      yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
                      curve="monotoneX"
                      enableArea
                      areaOpacity={0.12}
                      colors={["#009fe3"]}
                      pointSize={8}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      useMesh
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: isSmallScreen ? 35 : 0 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <DashboardCard>
                <SectionTitle title="Atenciones por municipio o comunidad" pill="Top 10" />
                <Box height={300}>
                  {hasBarData(communityChartData) ? (
                    <ResponsiveBar
                      data={communityChartData}
                      keys={["total"]}
                      indexBy="comunidad"
                      theme={nivoTheme}
                      margin={{ top: 20, right: 24, bottom: 70, left: 52 }}
                      padding={0.35}
                      colors={chartColors}
                      colorBy="indexValue"
                      borderRadius={8}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: isSmallScreen ? 35 : 20 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                      enableLabel={false}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle title="Principales diagnósticos" pill="Top 20" />
                <Box height={290}>
                  {hasPieData(diagnosisPieChartData) ? (
                    <ResponsivePie
                      data={diagnosisPieChartData}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 20, bottom: 70, left: 20 }}
                      innerRadius={0.58}
                      padAngle={1}
                      cornerRadius={4}
                      activeOuterRadiusOffset={8}
                      colors={chartColors}
                      borderWidth={1}
                      borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                      arcLabelsSkipAngle={diagnosisPieChartData.length > 12 ? 18 : 10}
                      arcLinkLabelsSkipAngle={diagnosisPieChartData.length > 12 ? 18 : 12}
                      legends={
                        diagnosisPieChartData.length <= 8
                          ? [
                              {
                                anchor: "bottom",
                                direction: "row",
                                translateY: 56,
                                itemWidth: 92,
                                itemHeight: 16,
                                symbolSize: 10,
                                symbolShape: "circle",
                              },
                            ]
                          : []
                      }
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle title="Distribución por sexo y rango de edad" pill="Perfil beneficiario" />
                <Box height={290}>
                  {hasBarData(sexAgeData, sexAgeKeys) ? (
                    <ResponsiveBar
                      data={sexAgeData}
                      keys={sexAgeKeys}
                      indexBy="grupo"
                      theme={nivoTheme}
                      margin={{ top: 20, right: 95, bottom: 55, left: 45 }}
                      padding={0.3}
                      groupMode="grouped"
                      colors={sexAgeKeys.length === 3 ? ["#d94841", "#004f7c", "#999999"] : ["#d94841", "#004f7c"]}
                      borderRadius={6}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: isSmallScreen ? 35 : 0 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                      enableLabel={false}
                      legends={[
                        {
                          dataFrom: "keys",
                          anchor: "right",
                          direction: "column",
                          translateX: 88,
                          itemWidth: 80,
                          itemHeight: 20,
                          symbolSize: 12,
                        },
                      ]}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle
                  title="Estado de atención"
                  pill="Referidos y seguimiento"
                  icon={<AssignmentTurnedInOutlinedIcon sx={{ mr: 1 }} />}
                />
                <Box height={290}>
                  {hasPieData(statusData) ? (
                    <ResponsivePie
                      data={statusData}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 20, bottom: 65, left: 20 }}
                      innerRadius={0.55}
                      padAngle={1}
                      cornerRadius={4}
                      activeOuterRadiusOffset={8}
                      colors={["#22a06b", "#f39313", "#d94841", "#009fe3"]}
                      borderWidth={1}
                      borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                      arcLabelsSkipAngle={10}
                      arcLinkLabelsSkipAngle={12}
                      legends={[
                        {
                          anchor: "bottom",
                          direction: "row",
                          translateY: 52,
                          itemWidth: 90,
                          itemHeight: 16,
                          symbolSize: 10,
                          symbolShape: "circle",
                        },
                      ]}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12}>
              <DashboardCard>
                <SectionTitle title="Detalle de atenciones de salud" pill="Tabla filtrable / exportable" />
                <Divider sx={{ mb: 2 }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  sx={{ mb: 1.5, flexWrap: "wrap" }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Lista de atendidos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mostrando {fromTableRow}-{toTableRow} de {totalTableRows} registros
                  </Typography>
                </Box>
                <TableContainer sx={{ width: "100%", maxHeight: 560, overflow: "auto" }}>
                  <Table stickyHeader size="small" aria-label="Detalle de atenciones de salud">
                    <TableHead>
                      <TableRow>
                        {[
                          "Fecha",
                          "Código",
                          "Beneficiario",
                          "Edad",
                          "Sexo",
                          "Comunidad",
                          "Centro",
                          MEDICAL_DEPLOYMENT_LABELS.singularShort,
                          "Motivo consulta",
                          "Diagnóstico",
                          "Tipo enfermedad",
                          "Médico",
                          "Estado atención",
                          "Atención",
                        ].map((column) => (
                          <TableCell
                            key={column}
                            sx={{
                              fontWeight: 900,
                              backgroundColor: "#004f7c",
                              color: "white",
                              whiteSpace: "nowrap",
                              textTransform: "uppercase",
                              letterSpacing: ".04em",
                              fontSize: 11,
                            }}
                          >
                            {column}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedTableRows.map((row) => (
                        <TableRow
                          key={row.codigo}
                          hover
                          sx={{
                            "&:nth-of-type(even)": {
                              backgroundColor:
                                theme.palette.mode === "dark" ? "rgba(255,255,255,.03)" : "#fbfdfe",
                            },
                          }}
                        >
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{formatDate(row.fecha)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.codigo}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.beneficiario}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.edad}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.sexo}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.comunidad}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.centro}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.jornada}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.motivoConsulta}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.diagnostico}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.tipoEnfermedad}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{row.medico}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              label={row.estadoAtencion}
                              color={statusColor[row.estadoAtencion] || "default"}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 900 }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                label={row.esReconsulta ? "Reconsulta" : formatNumeroAtencion(row.numeroAtencionPersona)}
                                color={row.esReconsulta ? "warning" : "default"}
                                size="small"
                                variant={row.esReconsulta ? "filled" : "outlined"}
                                sx={{ fontWeight: 900 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatNumeroAtencion(row.numeroAtencionPersona)}
                                {row.patientKeyConfidence ? ` · confianza ${row.patientKeyConfidence}` : ""}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                            No hay registros para los filtros seleccionados.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </DashboardCard>
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
};

export default DashboardSaludEmergencia;
